import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import buildHubUrl from "./buildHubUrl";
import CreateConnection from "./HubMatrix";
import { connectToHubUser } from "src/redux/slices/hub";
import { GetAllEmployees } from "src/redux/slices/employee";
import { GetEmployeeRoles } from "src/redux/slices/employeeRoles";
import { refreshEmployeeSession } from "src/redux/slices/employeeSession";

// 2026-08-24 · Hook global — escucha `role-permissions-changed/{restauranteId}`
// del UserHub. Backend lo emite al editar las actions de un rol; los JWT
// firmados que hay en circulación ya se revocaron en BD (RevokeAllForRoleAsync)
// pero el token del front sigue en localStorage con las actions viejas y el
// nav sigue mostrando lo que ya no le corresponde. Al recibir el evento:
//   • Si mi employeeId está en la lista de afectados → snackbar +
//     `logoutEmployee()` → limpia localStorage y despierta el kiosko para
//     re-fichar → nuevo JWT con actions actuales de BD.
//   • Si no soy afectado → no hago nada (el evento va al grupo restaurante
//     pero solo aplica a los empleados listados).
//
// Sin este hook el empleado seguiría operando con permisos viejos hasta
// que el backend rechace su próximo request con 401 (session revocada).
// El snackbar da contexto — sin él el user vería su sesión cerrarse
// sin explicación.
export default function useRolePermissionsHub() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector((s) => s.authJwt);
  const employeeId = useSelector((s) => s.employeeSession?.info?.employeeId);
  const seenRef = useRef(new Set());

  const restauranteId = user?.id;

  useEffect(() => {
    if (!restauranteId) return undefined;

    const handler = async (payload) => {
      if (!payload?.roleId) return;
      const sig = `${payload.roleId}:${payload.timestamp}`;
      if (seenRef.current.has(sig)) return;
      seenRef.current.add(sig);
      if (seenRef.current.size > 50) {
        const first = seenRef.current.values().next().value;
        seenRef.current.delete(first);
      }

      const affected = Array.isArray(payload.affectedEmployeeIds)
        ? payload.affectedEmployeeIds
        : [];

      // 2026-08-25 · Refetch SIEMPRE — el catálogo local de employees
      // trae `roleNavegation.actions` cacheado, y `useEmployeeRole`
      // (legacy) lo usa para poblar los `permissions` del NavBar. Sin
      // este refetch, tras el reload el nav sigue con las acciones
      // viejas y el user tenía que "cambiar de empleado y volver a
      // entrar" para forzar el fetch. Owner también necesita el
      // refetch para que su vista de admin de roles refleje el cambio.
      try {
        await Promise.all([
          dispatch(GetAllEmployees()),
          dispatch(GetEmployeeRoles()),
        ]);
      } catch { /* si falla, el reload/re-fichaje lo cubrirá */ }

      // No soy afectado — o no tengo sesión de empleado (soy owner) o mi
      // employeeId no está en la lista. Sin log ni notificación.
      if (!employeeId || !affected.includes(employeeId)) return;

      // 2026-08-25 · Refresh del JWT firmado para que `useCanDo` (que
      // lee `s.employeeSession.info.actions`) reciba las acciones
      // nuevas. El refetch de employees arriba solo actualiza
      // `useEmployeeRole` (nav) pero NO el `info` firmado — por eso
      // las rutas gate con `<RequireAction>` seguían bloqueando aunque
      // el admin acabara de marcar el permiso. Sin este refresh, el
      // empleado tenía que re-fichar con PIN para desbloquear.
      try {
        await dispatch(refreshEmployeeSession());
      } catch { /* falla silenciosa — al próximo request el 401 despertará el kiosko */ }

      enqueueSnackbar(
        `Tu rol "${payload.roleName || ""}" ha cambiado. Los permisos se han actualizado.`,
        { variant: "info", autoHideDuration: 4000 },
      );
    };

    const cleanup = CreateConnection(
      buildHubUrl("/hub/userhub", { restauranteId }),
      [{ id: `role-permissions-changed/${restauranteId}`, operator: handler }],
      dispatch,
      connectToHubUser,
      "user",
    );

    return cleanup;
  }, [restauranteId, employeeId, dispatch, enqueueSnackbar]);
}
